import { AgentTaskStatus } from '~/protos/agents'
import { TaskObject } from '~/types/TaskType'

export type MuleRefactorFlowStep =
  | 'selectRepository'
  | 'selectEndpoints'
  | 'getReady'
  | 'uploadInstructions'
  | 'refineFiles'
  | 'getCode'
  | 'error'

export interface SlideContext {
  taskStatus?: AgentTaskStatus
  selectedEndpointsCount?: number
  isSubmitting?: boolean
}

export interface SlideConfig {
  id: MuleRefactorFlowStep
  stepNumber: number
  title: string
  subtitle?: string
  buttonText: string
  isButtonEnabled?: (context: SlideContext) => boolean
  taskStatuses?: AgentTaskStatus[]
}

// Timeline-visible steps. The repo-selection step is rendered in the timeline
// for context, but only steps 1..5 map to in-task Swiper slides.
export const SLIDE_CONFIGS: SlideConfig[] = [
  {
    id: 'selectRepository',
    stepNumber: 0,
    title: 'Select Source Repository',
    buttonText: 'Next: Select Endpoints'
  },
  {
    id: 'selectEndpoints',
    stepNumber: 1,
    title: 'Select Endpoints',
    buttonText: 'Review Code Docs',
    isButtonEnabled: ({ taskStatus, selectedEndpointsCount, isSubmitting }) =>
      taskStatus === AgentTaskStatus.MIGRATION_ENDPOINTS_IDENTIFIED &&
      (selectedEndpointsCount ?? 0) >= 1 &&
      !isSubmitting,
    taskStatuses: [
      AgentTaskStatus.SUBMITTED,
      AgentTaskStatus.IN_PROGRESS,
      AgentTaskStatus.MIGRATION_ENDPOINTS_IDENTIFIED
    ]
  },
  {
    id: 'getReady',
    stepNumber: 2,
    title: 'Code Docs',
    subtitle: 'All Docs',
    buttonText: 'Upload Refactor Guidance',
    isButtonEnabled: ({ taskStatus, isSubmitting }) =>
      taskStatus === AgentTaskStatus.REFACTOR_EXISTING_DOCS_GENERATED && !isSubmitting,
    taskStatuses: [
      AgentTaskStatus.MIGRATION_ENDPOINTS_SELECTED,
      AgentTaskStatus.MIGRATION_DOCUMENT_GENERATED,
      AgentTaskStatus.REFACTOR_EXISTING_DOCS_GENERATED
    ]
  },
  {
    id: 'uploadInstructions',
    stepNumber: 3,
    title: 'Upload Instructions',
    buttonText: 'Start Refactoring',
    isButtonEnabled: ({ isSubmitting }) => !isSubmitting
    // No taskStatuses mapping: only reachable via the Next button from getReady.
  },
  {
    id: 'refineFiles',
    stepNumber: 4,
    title: 'Refactored Docs',
    subtitle: 'All Docs',
    buttonText: 'Generate Code',
    isButtonEnabled: ({ taskStatus, isSubmitting }) =>
      taskStatus === AgentTaskStatus.REFACTOR_API_PROPOSED && !isSubmitting,
    taskStatuses: [
      AgentTaskStatus.REFACTOR_USER_NOTES_ADDED,
      AgentTaskStatus.REFACTOR_PROCESSING_ENDPOINTS,
      AgentTaskStatus.REFACTOR_API_PROPOSING,
      AgentTaskStatus.REFACTOR_API_FEEDBACK_PROVIDED,
      AgentTaskStatus.REFACTOR_API_PROPOSED
    ]
  },
  {
    id: 'getCode',
    stepNumber: 5,
    title: 'Generate Code',
    subtitle: 'Generated Code',
    // No buttonText / isButtonEnabled: the proceed button is hidden on the
    // terminal step (see MuleRefactorFlowLayout).
    buttonText: '',
    taskStatuses: [
      AgentTaskStatus.REFACTOR_DOCS_GENERATING,
      AgentTaskStatus.REFACTOR_DOCS_GENERATED,
      AgentTaskStatus.REFACTOR_API_SPECS_GENERATED,
      AgentTaskStatus.REFACTOR_CODE_GENERATED
    ]
  }
]

// Error is a terminal state outside the main timeline but still owns a slide.
export const ERROR_STEP_NUMBER = 6

const STEP_BY_ID: Record<MuleRefactorFlowStep, number> = {
  selectRepository: 0,
  selectEndpoints: 1,
  getReady: 2,
  uploadInstructions: 3,
  refineFiles: 4,
  getCode: 5,
  error: ERROR_STEP_NUMBER
}

export function getSlideConfigById(id: MuleRefactorFlowStep): SlideConfig | undefined {
  return SLIDE_CONFIGS.find(config => config.id === id)
}

export function getSlideConfigByStepNumber(stepNumber: number): SlideConfig | undefined {
  return SLIDE_CONFIGS.find(config => config.stepNumber === stepNumber)
}

export function stepNumberOf(step: MuleRefactorFlowStep): number {
  return STEP_BY_ID[step]
}

// Maps a 1-based stepNumber to the Swiper slide index. Clamps `selectRepository`
// (stepNumber 0) to slide 0 so callers never hand Swiper a negative index.
export function slideIndexOf(step: MuleRefactorFlowStep): number {
  return Math.max(0, stepNumberOf(step) - 1)
}

/**
 * Derives which slide the user should see, purely from task data.
 *
 * Most statuses map 1:1 via SLIDE_CONFIGS. `REFACTOR_API_FEEDBACK_PROVIDED` is the
 * one exception: it means "user just submitted feedback" — empty feedback means
 * they're moving on to code generation; non-empty means they're regenerating docs.
 */
export function deriveStepFromTask(
  taskStatus: AgentTaskStatus | undefined,
  task: TaskObject | undefined
): MuleRefactorFlowStep {
  if (taskStatus === undefined) return 'selectEndpoints'
  if (taskStatus === AgentTaskStatus.ERRORED) return 'error'

  if (taskStatus === AgentTaskStatus.REFACTOR_API_FEEDBACK_PROVIDED) {
    // Without details we can't tell "moving forward" (empty comments → getCode)
    // from "regenerating" (non-empty comments → refineFiles), so default to
    // refineFiles which keeps the user on the same artifact.
    const responses = task?.details?.orderedStateResponses
    if (!responses?.length) return 'refineFiles'

    const latestFeedback = [...responses]
      .reverse()
      .find(r => r.taskStatus === AgentTaskStatus.REFACTOR_API_FEEDBACK_PROVIDED)
    if (!latestFeedback) return 'refineFiles'

    const comments = latestFeedback.request?.refactorStartUserComments?.generalComments ?? ''
    return comments.trim() === '' ? 'getCode' : 'refineFiles'
  }

  return getSlideConfigByTaskStatus(taskStatus)?.id ?? 'selectEndpoints'
}

export function getSlideConfigByTaskStatus(taskStatus: AgentTaskStatus): SlideConfig | undefined {
  return SLIDE_CONFIGS.find(config => config.taskStatuses?.includes(taskStatus))
}
